import { useTranslation } from 'react-i18next'

import { CreditIcon } from '@/components/icons/CreditIcon'
import { Text } from '@/components/ui/Text'
import { formatCreditAmount } from '@/lib/billing/formatCreditAmount'
import {
  SOCIAL_LISTENING_CREDIT_RATE,
  assistantRateRows,
  creditRateRows,
} from '@/lib/billing/creditRateTable'
import { newVideoMultiplier } from '@/lib/billing/room-pricing'

export interface MeteredPricingTablesProps {
  className?: string
}

function Credits(props: { value: number; label?: string; testId?: string }) {
  const { value, label, testId } = props
  return (
    <div data-testid={testId}>
      <span className="inline-flex items-center gap-1 tabular-nums text-sm text-foreground">
        <CreditIcon className="h-3 w-3 text-muted-foreground" />
        {label ?? formatCreditAmount(value)}
      </span>
    </div>
  )
}

/**
 * The notes under the enrolled price table: how the Assistant's weighted tokens count and the
 * new video rule. The prices themselves are rows of the flat table (`creditRateRows`). Shown
 * only when the org's metering mode is `on`.
 */
export function MeteredPricingTables(props: MeteredPricingTablesProps) {
  const { className } = props
  const { t } = useTranslation('app')

  const weightRow = assistantRateRows()[0]
  const weightMultiple = (exact: number) => Number((exact / weightRow.exact.inputCreditsPerMillion).toFixed(2))

  return (
    <div className={className} data-testid="metered-pricing-tables">
      <div className="flex flex-col gap-1">
        <Text size="xs" color="muted" data-testid="metered-assistant-weighting">
          {t('billing.metered.assistantBody', {
            output: weightMultiple(weightRow.exact.outputCreditsPerMillion),
            cached: weightMultiple(weightRow.exact.cachedInputCreditsPerMillion),
          })}
        </Text>
        <Text size="xs" color="muted" data-testid="metered-video-rule">
          {t('billing.metered.videoRule', { multiplier: newVideoMultiplier() })}
        </Text>
      </div>
    </div>
  )
}

/** i18n keys of the flat rate rows, by the row's `action` (mirrors the "How credits work" dialog). */
const CREDIT_RATE_KEYS: Record<string, { action: string; unit: string }> = {
  'Audience creation': { action: 'billing.plans.rates.audienceCreation', unit: 'billing.plans.rates.perTwin' },
  Study: { action: 'billing.plans.rates.study', unit: 'billing.plans.rates.perRespondent' },
  'Quick study': { action: 'billing.plans.rates.quickStudy', unit: 'billing.plans.rates.perRespondentPerQuestion' },
  Chat: { action: 'billing.plans.rates.chat', unit: 'billing.plans.rates.perMessage' },
  'Focus group': { action: 'billing.plans.rates.focusGroup', unit: 'billing.plans.rates.perMessage' },
  Image: { action: 'billing.plans.rates.image', unit: 'billing.plans.rates.perImage' },
  'Assistant weighted': { action: 'billing.plans.rates.assistantWeighted', unit: 'billing.plans.rates.perMillionWeightedTokens' },
  'Assistant typical': { action: 'billing.plans.rates.assistantTypical', unit: 'billing.plans.rates.perMessage' },
  'Chat levels': { action: 'billing.plans.rates.chatLevels', unit: 'billing.plans.rates.perMessage' },
  'Focus group levels': { action: 'billing.plans.rates.focusGroupLevels', unit: 'billing.plans.rates.perMessage' },
  'Social listening': { action: 'billing.plans.rates.socialListening', unit: 'billing.plans.rates.per150PostsAdded' },
}

export interface CreditRatesTableProps {
  /** Enrolled accounts price chat and focus groups in the level tables, so those rows leave. */
  enrolled: boolean
  className?: string
}

/**
 * The flat credit prices (audience creation, study, Quick study, chat and focus group per
 * message when not enrolled, images, social listening) read off the shared price list.
 */
export function CreditRatesTable(props: CreditRatesTableProps) {
  const { enrolled, className } = props
  const { t } = useTranslation('app')
  const rows = [...creditRateRows(enrolled), SOCIAL_LISTENING_CREDIT_RATE]

  return (
    <div className={className} data-testid="credit-rates-table">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-4 py-2.5 text-left text-xs font-medium text-foreground sm:w-[1%] sm:whitespace-nowrap">{t('billing.plans.action')}</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-foreground">{t('billing.plans.credits')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const keys = CREDIT_RATE_KEYS[row.action]
              return (
                <tr
                  key={row.action}
                  className="border-b border-border last:border-0"
                  data-testid={`credit-rates-row-${row.action.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <td className="px-4 py-2 text-sm text-foreground sm:whitespace-nowrap">
                    {keys ? t(keys.action as 'billing.plans.rates.study') : row.action}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-baseline gap-1.5">
                      <Credits value={row.rate} label={row.variants?.map((variant) => formatCreditAmount(variant.rate)).join(' / ')} />
                      <span className="text-xs font-normal text-muted-foreground">
                        {keys ? t(keys.unit as 'billing.plans.rates.perTwin') : row.unit}
                      </span>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
